/**
 * OpenTelemetry Browser Instrumentation
 *
 * Provides trace context propagation for distributed tracing.
 * Note: PostHog does not support OTLP traces (only logs), so we don't export
 * traces to a backend. Instead, we use OpenTelemetry for:
 * - Trace context propagation (traceparent/tracestate headers to API)
 * - Local span creation for debugging
 * - PostHog SDK handles analytics separately
 */

import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { Resource } from '@opentelemetry/resources';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import { trace, context, SpanStatusCode, type Span } from '@opentelemetry/api';

let initialized = false;

export interface TelemetryConfig {
  serviceName: string;
  serviceVersion: string;
  enabled?: boolean;
}

/**
 * Initialize OpenTelemetry for browser
 * Enables trace context propagation to backend APIs
 */
export function initTelemetry(config: TelemetryConfig): void {
  if (initialized || !config.enabled) {
    return;
  }

  const resource = new Resource({
    [ATTR_SERVICE_NAME]: config.serviceName,
    [ATTR_SERVICE_VERSION]: config.serviceVersion,
    'deployment.environment': import.meta.env.MODE,
  });

  const provider = new WebTracerProvider({
    resource,
  });

  // Use ZoneContextManager for async context propagation
  provider.register({
    contextManager: new ZoneContextManager(),
  });

  // Register automatic instrumentations
  registerInstrumentations({
    instrumentations: [
      new DocumentLoadInstrumentation(),
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: [
          // Allow trace propagation to our API
          /https?:\/\/.*\.workers\.dev.*/,
          /http:\/\/localhost:.*/,
        ],
        clearTimingResources: true,
      }),
      new UserInteractionInstrumentation({
        eventNames: ['click', 'submit'],
      }),
    ],
  });

  initialized = true;
}

/**
 * Get a tracer instance
 */
export function getTracer(name: string = 'platform-app') {
  return trace.getTracer(name);
}

/**
 * Create a span for manual instrumentation
 */
export function startSpan(name: string, fn: (span: Span) => void): void {
  const tracer = getTracer();
  tracer.startActiveSpan(name, (span) => {
    try {
      fn(span);
    } catch (error) {
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    } finally {
      span.end();
    }
  });
}

/**
 * Create a span for async operations
 */
export async function startAsyncSpan<T>(name: string, fn: (span: Span) => Promise<T>): Promise<T> {
  const tracer = getTracer();
  return tracer.startActiveSpan(name, async (span) => {
    try {
      const result = await fn(span);
      return result;
    } catch (error) {
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    } finally {
      span.end();
    }
  });
}

/**
 * Add attributes to the current span
 */
export function addSpanAttributes(attributes: Record<string, string | number | boolean>): void {
  const span = trace.getActiveSpan();
  if (span) {
    span.setAttributes(attributes);
  }
}

/**
 * Record an error in the current span
 */
export function recordError(error: Error): void {
  const span = trace.getActiveSpan();
  if (span) {
    span.recordException(error);
    span.setStatus({
      code: SpanStatusCode.ERROR,
      message: error.message,
    });
  }
}

export { trace, context, SpanStatusCode };
